import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Send, User, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

/**
 * User-to-user P2P internal send. Zero fees, instant, off-chain — the balance
 * moves from sender's spot wallet to recipient's spot wallet in a single atomic
 * DB write. Recipient lookup accepts an email OR a referral code.
 */
export default function SendDialog({ open, onOpenChange, spotBalances = {}, onSent }) {
  const [step, setStep] = useState(1);            // 1 = pick recipient/coin, 2 = review
  const [recipient, setRecipient] = useState('');
  const [preview, setPreview] = useState(null);   // {email_masked, name, referral_code}
  const [previewErr, setPreviewErr] = useState('');
  const [checking, setChecking] = useState(false);
  const [symbol, setSymbol] = useState('USDT');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep(1); setRecipient(''); setPreview(null); setPreviewErr('');
      setSymbol('USDT'); setAmount(''); setNote(''); setBusy(false);
    }
  }, [open]);

  const bal = Number(spotBalances[symbol] || 0);
  const symbols = Object.keys(spotBalances).filter(s => Number(spotBalances[s]) > 0);

  const checkRecipient = async () => {
    setPreview(null); setPreviewErr('');
    if (!recipient.trim()) return;
    setChecking(true);
    try {
      const r = await api.get(`/wallet/send/preview?recipient=${encodeURIComponent(recipient.trim())}`);
      setPreview(r.data);
    } catch (e) {
      setPreviewErr(e.response?.data?.detail || 'Not found');
    } finally { setChecking(false); }
  };

  const setPct = (p) => {
    if (bal <= 0) return;
    setAmount(String(Number((bal * p / 100).toFixed(8))));
  };

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!preview) { toast.error('Verify recipient first'); return; }
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (amt > bal) { toast.error(`Insufficient ${symbol} — you have ${bal.toFixed(6)}`); return; }
    setBusy(true);
    try {
      const r = await api.post('/wallet/send', {
        recipient: recipient.trim(), symbol, amount: amt, note: note.trim(),
      });
      toast.success(`Sent ${amt} ${symbol} to ${r.data.recipient_email}`);
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-md p-4 gap-3" data-testid="send-dialog">
        <DialogHeader className="pb-1">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Send className="w-4 h-4 text-[#FCD535]" /> Send to another user
          </DialogTitle>
        </DialogHeader>

        <div className="border border-[#0ECB81]/40 bg-[#0ECB81]/10 p-2 rounded-sm text-[11px] text-[#0ECB81]">
          Zero fees · Instant · Off-chain. Balance moves internally.
        </div>

        {/* Recipient */}
        <div>
          <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Recipient — email, @username, or referral code</div>
          <div className="flex gap-2">
            <Input value={recipient} onChange={e => { setRecipient(e.target.value); setPreview(null); setPreviewErr(''); }}
              placeholder="user@example.com  or  @yourname  or  CRYPTOX1"
              className="bg-[#0B0E11] border-[#2B3139] rounded-sm font-mono text-white text-sm focus-visible:border-[#FCD535]"
              data-testid="send-recipient-input" />
            <Button onClick={checkRecipient} disabled={checking || !recipient.trim()} size="sm"
              className="bg-[#2B3139] hover:bg-[#3A424C] text-[#FCD535] rounded-sm text-xs px-3"
              data-testid="send-check-recipient">
              {checking ? '…' : 'Verify'}
            </Button>
          </div>
          {preview && (
            <div className="border border-[#0ECB81]/40 bg-[#0ECB81]/10 rounded-sm px-2 py-1.5 mt-2 flex items-center gap-2 text-xs" data-testid="send-preview">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#0ECB81]" />
              <span className="text-white font-mono">{preview.email_masked}</span>
              {preview.name && <span className="text-[#B7BDC6]">({preview.name})</span>}
            </div>
          )}
          {previewErr && (
            <div className="border border-[#F6465D]/40 bg-[#F6465D]/10 rounded-sm px-2 py-1.5 mt-2 flex items-center gap-2 text-xs text-[#F6465D]" data-testid="send-preview-error">
              <AlertTriangle className="w-3.5 h-3.5" /> {previewErr}
            </div>
          )}
        </div>

        {/* Symbol */}
        <div>
          <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Coin</div>
          <div className="flex flex-wrap gap-1">
            {(symbols.length ? symbols : ['USDT']).map(s => (
              <button key={s} onClick={() => { setSymbol(s); setAmount(''); }}
                className={`text-xs uppercase font-mono px-2 py-1 border rounded-sm ${symbol === s ? 'bg-[#FCD535] text-[#0B0E11] border-[#FCD535]' : 'border-[#2B3139] text-[#B7BDC6] hover:border-[#FCD535]'}`}
                data-testid={`send-symbol-${s}`}>{s}</button>
            ))}
          </div>
        </div>

        {/* Available + Amount */}
        <div className="border border-[#2B3139] bg-[#0B0E11] px-3 py-1.5 rounded-sm flex items-center justify-between text-xs">
          <span className="text-[10px] uppercase tracking-wider text-[#848E9C]">Available</span>
          <span className="font-mono text-white" data-testid="send-available">{bal.toFixed(6)} {symbol}</span>
        </div>
        <div>
          <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Amount</div>
          <Input value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" type="number"
            className="bg-[#0B0E11] border-[#2B3139] rounded-sm font-mono text-white h-10 focus-visible:border-[#FCD535]"
            data-testid="send-amount-input" />
          <div className="flex gap-1 mt-1.5">
            {[25, 50, 75, 100].map(p => (
              <button key={p} onClick={() => setPct(p)} type="button"
                className="flex-1 text-[10px] text-[#848E9C] hover:text-white border border-[#2B3139] py-1 rounded-sm hover:border-[#FCD535]/60"
                data-testid={`send-pct-${p}`}>{p}%</button>
            ))}
          </div>
        </div>

        {/* Optional note */}
        <div>
          <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Note (optional)</div>
          <Input value={note} onChange={e => setNote(e.target.value)} maxLength={200}
            placeholder="e.g. Rent, dinner, etc."
            className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white text-sm h-9 focus-visible:border-[#FCD535]"
            data-testid="send-note-input" />
        </div>

        <Button onClick={submit} disabled={busy || !preview || !amount || parseFloat(amount) <= 0}
          className="w-full bg-[#FCD535] hover:bg-[#F0B90B] disabled:opacity-50 text-[#0B0E11] rounded-sm font-semibold h-10"
          data-testid="send-submit-btn">
          {busy ? 'Sending…' : `Send ${amount || '0'} ${symbol}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
